import { useState, useEffect, useRef } from 'react'
import { X, Printer, Loader2, FileText, Maximize2 } from 'lucide-react'

interface LabelPrintModalProps {
  pdfBlob: Blob
  trackingNumber?: string
  onClose: () => void
}

export default function LabelPrintModal({ pdfBlob, trackingNumber, onClose }: LabelPrintModalProps) {
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [isPrinting, setIsPrinting] = useState(false)
  const printFrameRef = useRef<HTMLIFrameElement>(null)

  // Crear URL del blob cuando se monta
  useEffect(() => {
    const url = URL.createObjectURL(pdfBlob)
    setPdfUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pdfBlob])

  const handlePrint = () => {
    if (!printFrameRef.current) return

    setIsPrinting(true)

    try {
      printFrameRef.current.contentWindow?.print()
    } catch (error) {
      console.error('Error al imprimir:', error)
      // Fallback: abrir en nueva ventana
      window.open(pdfUrl, '_blank')
    } finally {
      setIsPrinting(false)
    }
  }

  const handleOpenExternal = () => {
    window.open(pdfUrl, '_blank')
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <FileText className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Imprimir etiqueta
              </h2>
              {trackingNumber && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {trackingNumber}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Vista previa del PDF - ocupa todo el espacio disponible */}
        <div className="flex-1 bg-slate-100 dark:bg-slate-900 p-4 min-h-0">
          {pdfUrl ? (
            <iframe
              ref={printFrameRef}
              src={pdfUrl}
              className="w-full h-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white"
              title="Vista previa de etiqueta"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={handleOpenExternal}
            className="flex items-center gap-2 px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
            Abrir en ventana nueva
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting || !pdfUrl}
            className="flex items-center gap-2 px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors"
          >
            {isPrinting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Printer className="w-5 h-5" />
            )}
            Imprimir
          </button>
        </div>
      </div>
    </div>
  )
}
